import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Stack,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/system';
import strings from 'common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import Form from 'components/Form';
import { ChangeEvent, MouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import { getNewBookingSendEmailDrawerWidth } from 'utils/validators/HelperFunctions';
import { EmailAttachmentVisibility } from './emailAttachmentOptions';
import { NewBookingSendEmailFormComponent } from './SendEmailFormComponent';

const MAX_FILE_BYTES = 10 * 1024 * 1024;

const checkboxLabel = (text: string) => (
  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{text}</Typography>
);

export const EmailAttachmentCheckboxes = ({
  shouldAttachContract,
  shouldAttachProposal,
  shouldAttachPi,
  shouldAttachEnquiryDetails,
  sx,
}: EmailAttachmentVisibility & { sx?: SxProps<Theme> }) => (
  <Box display="flex" flexDirection="column" gap={0.25} sx={sx}>
    {shouldAttachContract && (
      <FormControlLabel
        control={<Checkbox size="small" id="attachContract" />}
        label={checkboxLabel(strings.attachContract)}
        sx={{ ml: 0, mr: 0 }}
      />
    )}
    {shouldAttachEnquiryDetails && (
      <FormControlLabel
        control={<Checkbox size="small" id="attachEnquiry" />}
        label={checkboxLabel(strings.attachEnquiry)}
        sx={{ ml: 0, mr: 0 }}
      />
    )}
    {shouldAttachProposal && (
      <FormControlLabel
        control={<Checkbox size="small" id="attachProposal" />}
        label={checkboxLabel(strings.attachProposal)}
        sx={{ ml: 0, mr: 0 }}
      />
    )}
    {shouldAttachPi && (
      <FormControlLabel
        control={<Checkbox size="small" id="attachPI" />}
        label={checkboxLabel(strings.attachPI)}
        sx={{ ml: 0, mr: 0 }}
      />
    )}
  </Box>
);

export const EmailFileAttachments = ({
  files,
  onFilesChange,
  disabled = false,
}: {
  files: File[];
  onFilesChange: (files: File[]) => void;
  disabled?: boolean;
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState('');

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    if (!selected.length) return;
    const tooLarge = selected.find(file => file.size > MAX_FILE_BYTES);
    if (tooLarge) {
      setError(strings.booking_logs_file_too_large);
      return;
    }
    setError('');
    const byName = new Map(files.map(file => [file.name, file]));
    selected.forEach(file => byName.set(file.name, file));
    onFilesChange(Array.from(byName.values()));
  };

  return (
    <>
      <input ref={fileInputRef} type="file" hidden multiple onChange={handleFileChange} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mt: 1.5,
          flexWrap: 'wrap',
        }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AttachFileIcon />}
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
          sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}>
          {strings.actualization_attach_file}
        </Button>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {strings.booking_logs_max_file_size}
        </Typography>
        {files.map(file => (
          <Chip
            key={file.name}
            size="small"
            label={file.name}
            onDelete={disabled ? undefined : () => onFilesChange(files.filter(item => item.name !== file.name))}
            deleteIcon={<CloseIcon />}
            sx={{ maxWidth: 220, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
          />
        ))}
      </Box>
      {error && (
        <Typography sx={{ fontSize: 12, color: 'error.main', mt: 0.75 }}>{error}</Typography>
      )}
    </>
  );
};

interface NewBookingSendEmailComponentProps {
  openDrawer: boolean;
  formRef: any;
  handleCloseDrawer: () => void;
  hasError: any;
  pageResponse: any;
  /** Return true after a successful send to keep submit locked until the next opening. */
  handleSave: (event: MouseEvent<HTMLButtonElement>) => Promise<boolean | void>;
  attachment: any;
  deletedImage: any;
  getFileDetails: any;
  submitButtonText?: string;
  shouldAttachContract?: boolean;
  shouldAttachProposal?: boolean;
  shouldAttachPi?: boolean;
  shouldAttachEnquiryDetails?: boolean;
  /** Disable submit while a save request is in flight. */
  disabled?: boolean;
}

const NewBookingSendEmailComponent = ({
  openDrawer,
  formRef,
  handleCloseDrawer,
  hasError,
  pageResponse,
  handleSave,
  attachment,
  getFileDetails,
  shouldAttachContract = false,
  shouldAttachProposal = false,
  shouldAttachPi = false,
  shouldAttachEnquiryDetails = false,
  disabled = false,
}: NewBookingSendEmailComponentProps) => {
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const busy = disabled || isSubmitting;

  useEffect(() => {
    if (openDrawer) {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [openDrawer]);

  const handleSend = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (disabled || submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    let sent = false;
    try {
      sent = (await handleSave(event)) === true;
    } finally {
      if (!sent) {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    }
  };

  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const files: File[] = Array.isArray(attachment) ? attachment : [];

  return (
    <DrawerMenu
      dropBack={false}
      width={getNewBookingSendEmailDrawerWidth(screenSizeDownSm, screenSizeDownMd)}
      open={openDrawer}
      handleClose={() => { if (!busy) handleCloseDrawer(); }}
      closeButton={true}
      flexColumn
      noPadding
      title={strings.bookings_text_send_email}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          width: '100%',
          height: '100%',
        }}>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.5, pt: 2, pb: 1 }}>
          <Grid container spacing={1.5} sx={{ mt: 0 }}>
            <Form
              hasError={hasError}
              ref={formRef as RefObject<Form>}
              model={NewBookingSendEmailFormComponent(strings)}
              values={pageResponse}
            />
          </Grid>
          <Box sx={{ mt: 1 }}>
            <EmailAttachmentCheckboxes
              shouldAttachContract={shouldAttachContract}
              shouldAttachProposal={shouldAttachProposal}
              shouldAttachPi={shouldAttachPi}
              shouldAttachEnquiryDetails={shouldAttachEnquiryDetails}
            />
          </Box>
          <EmailFileAttachments
            files={files}
            disabled={busy}
            onFilesChange={next => getFileDetails?.(next)}
          />
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
            pt: 1.5,
            pb: 2,
            mt: 1,
            px: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
          }}>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" color="primary" onClick={handleCloseDrawer} disabled={busy}>
              {strings.cancelText}
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSend}
              disabled={busy}>
              {strings.bookings_text_send_email}
            </Button>
          </Stack>
        </Box>
      </Box>
    </DrawerMenu>
  );
};
export default NewBookingSendEmailComponent;
